/**
 * useContentLocale — owns the `?contentLocale=` URL state naming which
 * CONTENT language the admin is listing, creating into and previewing. The
 * sibling of `useCatalogTab`'s `?tab` and `useEditStep`'s `?step`, and the
 * single reader of the axis the register calls `contentLocale`.
 *
 * WHY A SECOND AXIS AND NOT `useLanguage`: `useLanguage` answers how the TOOL
 * is labelled and is structurally `'fi' | 'en'`, so it cannot express `sv` at
 * all. The content axis is `'fi' | 'sv' | 'en'` and answers which language's
 * content the author is working on. One value served both until this hook
 * existed, which is why an English-labelled tool created `en` rows: the create
 * paths were reading the reader's axis to decide the WORK's language.
 *
 * WHY THE URL AND NOT COMPONENT STATE OR A STORED PREFERENCE: which language's
 * content you are looking at changes what the list shows, so it should survive
 * a reload and be linkable — an author saying "the Swedish catalog is empty"
 * wants to send someone the view they are looking at. It also sits in the same
 * URL-derived layer as `?doc` / `?tab` / `?step`, so opening an item (which
 * unmounts the catalog branch) cannot discard it. Nothing is persisted: no
 * cookie, no `localStorage`, no `sessionStorage`, no session row, so there is
 * no second author and nothing to drift. The prototype this surface derives
 * from stores nothing either, measured across all three storages.
 *
 * DEFAULT `fi`, DELIBERATELY NOT THE UI LANGUAGE. The content locale is a
 * property of the work, not of the reader, so making it follow the reader would
 * rebuild the very conflation this hook undoes. It is also the only locale
 * guaranteed to hold content, so an English-labelled tool opens on a catalog
 * that has rows rather than on an empty list that looks broken.
 *
 * VALIDATE AND DEGRADE OFF, like `?step` and `?tab`: anything outside the
 * closed set reads as `fi`. The prototype does NOT do this — `?locale=de`
 * blanks its switch and empties every group with no error — and that silence is
 * a defect this feature does not copy. The default writes NO param, so the
 * clean URL is the default state.
 */

import { useSearchParams } from 'react-router';

import type { MessageKeys } from '../../i18n/messages/index.js';
import type { CreateContentInput } from './hooks/adminContentMutations.js';

/**
 * The content-language axis, LIFTED from the wire contract rather than
 * restating the alphabet, so the admin cannot offer a locale the api would
 * refuse and cannot silently lag one it starts admitting.
 */
export type ContentLocale = CreateContentInput['locale'];

/**
 * The closed set the control offers, in the prototype's order (Suomi, Ruotsi,
 * Englanti). `satisfies` is the load-bearing word: it holds this list to the
 * wire contract, which is what makes "the control cannot offer a value the api
 * would refuse" a compile-time fact rather than a review promise.
 */
export const CONTENT_LOCALES = ['fi', 'sv', 'en'] as const satisfies readonly ContentLocale[];

/** Absent or unrecognised `?contentLocale=` lands on Finnish. */
export const DEFAULT_CONTENT_LOCALE: ContentLocale = 'fi';

/** Validate and degrade off: anything outside the closed set reads as `fi`. */
export function toContentLocale(value: string | null | undefined): ContentLocale {
  return (CONTENT_LOCALES as readonly string[]).includes(value ?? '')
    ? (value as ContentLocale)
    : DEFAULT_CONTENT_LOCALE;
}

/**
 * The message stating a ROW's own language, one key per language.
 *
 * WHY A KEY PER LANGUAGE INSTEAD OF ONE KEY WITH A `{language}` SLOT: Finnish
 * inflects the language name inside the sentence -- `suomi` becomes `suomeksi`
 * -- so an interpolated key cannot produce a grammatical statement. The shell's
 * own language toggle already carries two keys for exactly this reason.
 *
 * A `Record` keyed by `ContentLocale` rather than a loose lookup, so a locale
 * the wire starts admitting fails to typecheck HERE instead of silently going
 * unstated on screen.
 */
const OPEN_ROW_LANGUAGE_KEYS: Record<ContentLocale, MessageKeys> = {
  fi: 'cms.contentLocale.openRow.fi',
  sv: 'cms.contentLocale.openRow.sv',
  en: 'cms.contentLocale.openRow.en',
};

/**
 * The key stating `rowLocale`'s language, or `null` when there is nothing this
 * surface can say TRUTHFULLY -- either the row's locale is unknown (its list
 * entry has not resolved) or it is a value outside the closed set. Silence is
 * the honest answer there; naming the wrong language would be worse than saying
 * nothing, and falling back to Finnish is exactly that mistake.
 */
export function openRowLanguageKey(rowLocale: string | undefined): MessageKeys | null {
  if (rowLocale === undefined) return null;
  return Object.hasOwn(OPEN_ROW_LANGUAGE_KEYS, rowLocale)
    ? OPEN_ROW_LANGUAGE_KEYS[rowLocale as ContentLocale]
    : null;
}

export interface ContentLocaleState {
  /**
   * The locale named by `?contentLocale=`, degraded to `fi` when absent or
   * unknown. Every admin surface that lists, creates or resolves categories
   * takes its locale from here.
   */
  readonly contentLocale: ContentLocale;
  /** Commit a switch to the URL, preserving every other param. */
  readonly onContentLocaleChange: (locale: ContentLocale) => void;
}

export function useContentLocale(): ContentLocaleState {
  const [searchParams, setSearchParams] = useSearchParams();

  return {
    contentLocale: toContentLocale(searchParams.get('contentLocale')),
    onContentLocaleChange: (locale) => {
      // The route's param-write idiom: mutate a clone of the LIVE params via
      // the functional updater and replace, so `?doc` / `?tab` / `?step` ride
      // along and a switch adds no Back-history step.
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          if (locale === DEFAULT_CONTENT_LOCALE) {
            next.delete('contentLocale');
          } else {
            next.set('contentLocale', locale);
          }
          return next;
        },
        { replace: true },
      );
    },
  };
}
